import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react'
import type { GridApi } from '@/components/grid/types'
import { CAPACITY_FIELD, DESCRIPTION_FIELD, DRAFT_ID_START } from './constants'
import type { DraftPipe, HydraulicTreeRow } from './types'
import { cellRowId, dropDraftsUnder, toDraftCapacity } from './save-rules'

interface UseHydraulicDraftsArgs {
  bulkEditMode: boolean
  isViewOnly: boolean
  authInterfaceId: unknown
  gridApiRef: MutableRefObject<GridApi<HydraulicTreeRow> | null>
  rowDataRef: MutableRefObject<HydraulicTreeRow[]>
}

/**
 * The staged creates.
 *
 * They live outside `gridData` so a refetch — which rebuilds the tree from the
 * server rows — cannot drop them; the caller splices them back into the tree
 * for display. Nothing is POSTed until the toolbar's Save runs.
 */
const useHydraulicDrafts = ({
  bulkEditMode,
  isViewOnly,
  authInterfaceId,
  gridApiRef,
  rowDataRef,
}: UseHydraulicDraftsArgs) => {
  const [drafts, setDrafts] = useState<DraftPipe[]>([])
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const draftIdRef = useRef(DRAFT_ID_START)

  /**
   * Every change to the staged list goes through here: the next list is
   * built from the ref and written to it at once, as well as to the state.
   *
   * A save reads `draftsRef` straight after committing the open editor. The
   * commit lands in `updateDraftField`, and a state update alone reaches the
   * ref only on the next render — after the save has already read the old
   * value. A rename the autosave committed by closing the editor was lost
   * that way (SCRUM-1032 review). Built from the ref rather than from
   * React's `prev` for the same reason: two changes before a render must
   * both survive.
   */
  const commitDrafts = useCallback(
    (next: (current: DraftPipe[]) => DraftPipe[]) => {
      const list = next(draftsRef.current)
      if (list === draftsRef.current) return
      draftsRef.current = list
      setDrafts(list)
    },
    [],
  )

  // Cells a save refused to write, keyed `${rowId}::${field}`.
  const [invalidCells, setInvalidCells] = useState<Set<string>>(new Set())

  // The row is only in the DOM after the drafts state has rendered, so both
  // the scroll and the editor wait a tick.
  const startEditingDraft = useCallback(
    (path: string[]) => {
      const rowId = path.join('/')
      setTimeout(() => {
        gridApiRef.current?.scrollToRow(rowId)
        gridApiRef.current?.startEditingCell(rowId, DESCRIPTION_FIELD)
      }, 0)
    },
    [gridApiRef],
  )

  const addDraft = useCallback(
    (parent: { id: number; level: number; interfaceId: number } | null) => {
      if (isViewOnly) return
      gridApiRef.current?.stopEditing()

      const id = draftIdRef.current
      draftIdRef.current -= 1
      const draft: DraftPipe = {
        id,
        interfaceId: parent?.interfaceId || Number(authInterfaceId),
        parentId: parent?.id ?? 0,
        // Mainlines are level 1, and pipes nest to MAX_PIPE_LEVEL from there.
        level: parent ? parent.level + 1 : 1,
        description: '',
      }
      commitDrafts((prev) => [...prev, draft])

      // A collapsed pipe would hide the draft (and the editor) it just
      // gained. The tree keys expansion off the row id, which is the path.
      const parentPath = parent
        ? rowDataRef.current.find((row) => row.id === parent.id)?.path
        : undefined
      if (parentPath) gridApiRef.current?.setExpanded(parentPath.join('/'), true)
      startEditingDraft([...(parentPath ?? []), `__DRAFT_${id}__`])
    },
    [authInterfaceId, isViewOnly, startEditingDraft, gridApiRef, rowDataRef, commitDrafts],
  )

  /**
   * Drops a staged draft, leaving the rest of the batch alone. Its staged
   * descendants go with it: they name it as their parent, and nothing else
   * could ever give them a real one.
   */
  const handleDiscardDraft = useCallback((id: number) => {
    const doomed = new Set<number>()
    commitDrafts((prev) => {
      const kept = dropDraftsUnder(prev, new Set([String(id)]), true)
      prev.forEach((draft) => {
        if (!kept.includes(draft)) doomed.add(draft.id)
      })
      return kept.length === prev.length ? prev : kept
    })
    setInvalidCells((prev) => {
      const next = new Set(
        [...prev].filter((key) => !doomed.has(Number(cellRowId(key)))),
      )
      return next.size === prev.size ? prev : next
    })
  }, [commitDrafts])

  /**
   * Drops the drafts staged under pipes that are gone — a deleted pipe and
   * its sub-pipes, as string ids — and under those drafts in turn. Nothing
   * could ever give them a real parent, and left staged they would hold the
   * step with an error the user cannot fix (SCRUM-1032 review).
   */
  const discardDraftsUnder = useCallback(
    (goneIds: ReadonlySet<string>) => {
      const doomed = new Set<string>()
      commitDrafts((prev) => {
        const kept = dropDraftsUnder(prev, goneIds)
        prev.forEach((draft) => {
          if (!kept.includes(draft)) doomed.add(String(draft.id))
        })
        return kept.length === prev.length ? prev : kept
      })
      if (doomed.size === 0) return
      setInvalidCells((prev) => {
        const next = new Set([...prev].filter((key) => !doomed.has(cellRowId(key))))
        return next.size === prev.size ? prev : next
      })
    },
    [commitDrafts],
  )

  /**
   * Typing into a staged draft stages the value — it is not written until the
   * toolbar Save runs, exactly like an edit to an existing row.
   */
  const updateDraftField = useCallback(
    (id: number, field: string, newValue: unknown) => {
      const value =
        field === CAPACITY_FIELD
          ? { capacity: toDraftCapacity(newValue) }
          : { description: String(newValue ?? '').trim() }

      commitDrafts((prev) =>
        prev.map((draft) => (draft.id === id ? { ...draft, ...value } : draft)),
      )
      setInvalidCells((prev) => {
        const key = `${id}::${field}`
        if (!prev.has(key)) return prev
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    },
    [commitDrafts],
  )

  const resetDrafts = useCallback(() => {
    draftsRef.current = []
    setDrafts([])
    draftIdRef.current = DRAFT_ID_START
    setInvalidCells(new Set())
  }, [])

  // Leaving bulk edit ends the staged batch, whichever way it ended.
  useEffect(() => {
    if (bulkEditMode) return
    resetDrafts()
  }, [bulkEditMode, resetDrafts])

  return {
    drafts,
    draftsRef,
    setDrafts,
    addDraft,
    handleDiscardDraft,
    discardDraftsUnder,
    updateDraftField,
    resetDrafts,
    invalidCells,
    setInvalidCells,
  }
}

export default useHydraulicDrafts
